import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import type { Claim } from '@/types/api';
import { ApiError } from '@/lib/api';
import { errorMessage } from '@/lib/copy';
import { formatRemaining } from '@/lib/format';
import { useNow } from '@/lib/hooks';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { toast } from '@/components/ui/toast';
import { useClaim, useRelease } from './queries';

/* ---------------------------------------------------------------------------
   Check-out, drawn honestly.

   There is no `in review` status. A chat that is queued, a chat that is checked
   out and a chat resting between review turns are all `pending review` — the
   claim column is what tells them apart, and the claim is
   `assigned_expert_user_id` plus a lease, nothing more. So this control never
   pretends to move a status: it takes, refreshes or gives back an assignment.

   Three states, three different affordances:

     unclaimed        → Claim. The compare-and-set is guarded on the column
                        being NULL and the status being `pending review`.
     held by you      → Open, with the lease countdown beside it, and Release.
                        Re-claiming extends the lease, so a refresh is a 200.
     held by another  → nothing to press. The holder is named, because the
                        server tells us who and "try again" would not.
--------------------------------------------------------------------------- */

export function ClaimState({ claim, className }: { claim: Claim; className?: string }) {
  const live = Boolean(claim);
  const now = useNow(live, 15_000);

  if (!claim) {
    return <span className={cn('font-mono text-xs text-ink-3', className)}>unclaimed</span>;
  }

  const remaining = formatRemaining(claim.expires_at, now);
  const expired = remaining === 'expired';

  return (
    <span className={cn('flex flex-col items-start gap-0.5', className)}>
      <span className={cn('font-mono text-xs', claim.is_mine ? 'text-live' : 'text-ink-2')}>
        {claim.is_mine ? 'you' : claim.expert_display_name}
      </span>
      <span className={cn('font-mono text-tiny tabular-nums', expired ? 'text-warn' : 'text-ink-3')}>
        {expired ? 'lease expired' : remaining}
      </span>
    </span>
  );
}

export function ClaimControl({
  chatId,
  claim,
  /** Only `pending review` accepts a first claim; the server enforces it. */
  claimable,
  size = 'sm',
  onClaimed,
}: {
  chatId: number;
  claim: Claim;
  claimable: boolean;
  size?: 'sm' | 'md';
  onClaimed?: () => void;
}) {
  const navigate = useNavigate();
  const claimMutation = useClaim(chatId);
  const releaseMutation = useRelease(chatId);

  const mine = claim?.is_mine === true;
  const heldByOther = Boolean(claim) && !mine;

  const onError = React.useCallback((error: unknown) => {
    if (!(error instanceof ApiError)) return;
    const holder = error.claim;
    toast(
      'error',
      holder && !holder.is_mine ? `${holder.expert_display_name} holds this chat` : 'Could not check that chat out',
      errorMessage(error.code, error.message),
    );
  }, []);

  if (heldByOther) {
    return (
      <span className="font-mono text-tiny text-ink-3">held by {claim!.expert_display_name}</span>
    );
  }

  if (mine) {
    return (
      <span className="flex items-center gap-1.5">
        <Button size={size} variant="secondary" onClick={() => navigate(`/chats/${chatId}`)}>
          Open review
        </Button>
        <Button
          size={size}
          variant="ghost"
          loading={releaseMutation.isPending}
          onClick={() =>
            releaseMutation.mutate(undefined, {
              onSuccess: () => toast('info', 'Released', 'The chat is back in the queue as unclaimed. The project stays frozen.'),
              onError,
            })
          }
        >
          Release
        </Button>
      </span>
    );
  }

  return (
    <Button
      size={size}
      variant="primary"
      disabled={!claimable}
      loading={claimMutation.isPending}
      title={claimable ? 'Take the assignment and open the review' : 'Only a chat in pending review can be checked out'}
      onClick={() =>
        claimMutation.mutate(undefined, {
          onSuccess: () => {
            onClaimed?.();
            navigate(`/chats/${chatId}`);
          },
          onError,
        })
      }
    >
      Claim
    </Button>
  );
}
